import { defineStore } from 'pinia'
import { $api } from '~/composables/api'
import { JoinStep } from '~/types/join'
import type { CheckEmailResponse, JoinOrderConfirmation, JoinSession, RecruitRecord, RecruitPersonalInfo, RecruitAddress, VatInfo, VerifyCustomerRequest, JoinKitOption, JoinKitOptionsResponse, WelcomeBoxEligibility } from '~/types/join'
import type { Demonstrator } from '~/types/account'
import { UserRole } from '~/types/account'
import type { CheckoutProperties } from '~/types/persistent'
import { useCheckoutStore } from '~/stores/checkout'
import { usePersistentStore } from '~/stores/persistent'
import { useAgentStore } from '~/stores/agent'
import { useLocaleStore } from '~/stores/locale'
import { ImpersonationRefreshError } from '~/types/agent'
import { setRecruitCookie, clearRecruitCookie } from '~/utils/auth'
import { getApiErrorData, readApiFailure, getErrorStatus, hasApiErrorCode, toError, type StampinupError } from '~/utils/http/api-error'
import { addCommonQueryParam } from '~/utils/general'
import { isFieldRequired } from '~/utils/locale'

// Raw API-response shapes (backend returns flat/loose JSON we map into typed models).
interface CheckEmailRaw {
  exists?: boolean
  firstName?: string
  lastName?: string
  isActiveDemonstrator?: boolean
  isWithinCoolDown?: boolean
  cooldownEndDate?: string
  errors?: { message?: string }[]
}
interface RecruitRaw {
  recruit?: RecruitRaw
  firstName?: string
  lastName?: string
  email?: string
  homePhone?: string
  gender?: string
  birthday?: string
  formattedBirthday?: string
  identification?: string
  secondaryLanguage?: string
  birthCity?: string
  birthDepartment?: string
  birthCountry?: string
  nationality?: string
  driversLicense?: string
  driversLicenseState?: string
  hasGstNumber?: boolean
  gstNumber?: string
  mailingAddress?: RecruitAddress | null
  shippingAddress?: RecruitAddress | null
  signedAgreements?: boolean
  demoDirectoryAgreement?: boolean
  uplineId?: number | null
  cart?: { items?: Array<{ itemType?: string; productID?: string; productId?: string; itemId?: string }> }
}

const createEmptySession = (teamLeaderId: string | null = null): JoinSession => ({
  email: '',
  firstName: '',
  lastName: '',
  teamLeaderId,
  teamLeader: null,
  currentStep: JoinStep.Entry,
  sessionId: crypto.randomUUID(),
  agreementsAccepted: false,
  directoryOptIn: false,
  myDetailsSaved: false,
  vatInfoCompleted: false,
  selectedKitItemId: null,
  hasMultipleKits: false,
  invoiceId: null,
})

const GENERIC_ERROR = 'error.somethingWrong'

// The Backend join submit refuses a recruit with no tax ID in the countries that require one. AB#64917
const RECRUIT_IDENTIFICATION_REQUIRED = 'RecruitIdentificationRequired'

// The throwaway test recruit an agent joins as, ported from nuxt2's AgentHeader
// newRecruit(): a random 15-character gmail local part, name "agent agent", phone
// 12345678. Named here so the API body and the local session cannot drift. AB#64477
const TEST_RECRUIT_EMAIL_LENGTH = 15
const TEST_RECRUIT_EMAIL_CHARS = 'abcdefghijklmnopqrstuvwxyz1234567890'
const TEST_RECRUIT = { firstName: 'agent', lastName: 'agent', homePhone: '12345678' } as const

const generateTestRecruitEmail = () => {
  const localPart = Array.from(
    { length: TEST_RECRUIT_EMAIL_LENGTH },
    () => TEST_RECRUIT_EMAIL_CHARS[Math.floor(Math.random() * TEST_RECRUIT_EMAIL_CHARS.length)]
  ).join('')
  return `${localPart}@gmail.com`
}

// The fr-fr birth-department field is picked from a list whose items are shaped
// `{ value: '76', text: '76 - Seine-Maritime' }`. The control can surface the
// display text rather than the code, but the backend column is only char(3), so
// we must send the code alone. Split on ' - ' (never digit-extraction): valid
// codes include 2A/2B (Corsica) and 3-char overseas codes (971–976). Idempotent —
// a value that is already a bare code passes through unchanged.
const toDepartmentCode = (v?: string) => v?.split(' - ')[0]?.trim()

const withDepartmentCode = (info: RecruitPersonalInfo): RecruitPersonalInfo =>
  info.birthDepartment
    ? { ...info, birthDepartment: toDepartmentCode(info.birthDepartment) }
    : info

export const useJoinStore = defineStore('join', () => {
  const session = ref<JoinSession>(createEmptySession())
  const checkEmailResult = ref<CheckEmailResponse | null>(null)
  const confirmation = ref<JoinOrderConfirmation | null>(null)
  const recruit = ref<RecruitRecord | null>(null)
  const kitOptions = ref<JoinKitOption[] | null>(null)
  const offerEndDate = ref<string | null>(null)
  const welcomeBoxEligibility = ref<WelcomeBoxEligibility | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  // The submit's RecruitIdentificationRequired message, kept apart from `error` because the
  // payment guard redirects to my-details and fetchRecruit clears `error` on the way in. The
  // my-details page shows it until personal info is saved. AB#64917
  const identificationRejection = ref<string | null>(null)

  // Every join action surfaces the first backend error message and falls back to the
  // generic key; each caller keeps its own return value. The failure is read once here and
  // handed back with the text that was shown, for a caller that also reports it.
  const recordFailure = (e: unknown) => {
    const failure = readApiFailure(e)
    const message = failure.backendMessages[0] ?? GENERIC_ERROR
    error.value = message
    return { message, listedErrors: failure.listedErrors }
  }

  const { $appInsights } = useNuxtApp()

  // Emit a join-funnel event. Telemetry must never break the flow, so calls are
  // fully optional-chained (also keeps SSR / unmocked tests safe).
  const trackJoinEvent = (name: string, properties: Record<string, string> = {}) => {
    $appInsights?.trackEvent?.({ name, properties })
  }

  // The backend already logs API errors, so we deliberately do NOT re-capture them.
  // Only surface genuinely unexpected client-side failures — ones with no HTTP
  // response and no API-error payload — so they aren't silently swallowed by a catch.
  const captureUnexpected = (e: unknown, listedErrors: StampinupError[] | undefined, context: string) => {
    if (getErrorStatus(e) === undefined && !listedErrors) {
      $appInsights?.trackException?.({
        exception: toError(e),
        properties: { context },
      })
    }
  }

  const isExistingCustomer = computed(() => checkEmailResult.value?.exists === true)
  const isActiveDemonstrator = computed(() => checkEmailResult.value?.isActiveDemonstrator === true)
  const isWithinCooldown = computed(() => checkEmailResult.value?.isWithinCooldown === true)
  const cooldownEndDate = computed(() => checkEmailResult.value?.cooldownEndDate)
  const firstName = computed(() => checkEmailResult.value?.firstName ?? session.value.firstName ?? '')

  const setRecruitName = (first: string, last: string) => {
    session.value.firstName = first
    session.value.lastName = last
  }
  const hasConfirmedTeamLeader = computed(() => session.value.teamLeader?.confirmedAt !== null && session.value.teamLeader !== null)
  const hasSelectedKit = computed(() => !!session.value.selectedKitItemId)
  const selectedKit = computed(() => kitOptions.value?.find(k => k.itemId === session.value.selectedKitItemId) ?? null)
  const agreementsAccepted = computed(() => session.value.agreementsAccepted)
  // The one my-details completeness rule: session.myDetailsSaved plus every personal-info field
  // the country's join config (idaPersonalInfo) marks required (see isFieldRequired). Join
  // verify-customer copies an existing customer's default address onto the recruit, so the flag
  // alone (set from that address on a full page load) does not prove the applicant filled in
  // the form. AB#64917
  const localeStore = useLocaleStore()
  const personalInfoComplete = computed(() => {
    const info = recruit.value?.personalInfo as Record<string, unknown> | undefined
    return localeStore.idaPersonalInfo()
      .filter(isFieldRequired)
      .every(field => String(info?.[field.name] ?? '').trim() !== '')
  })
  const myDetailsCompleted = computed(() => session.value.myDetailsSaved && personalInfoComplete.value)
  const vatInfoCompleted = computed(() => session.value.vatInfoCompleted)

  const mapCheckEmailResponse = (response: CheckEmailRaw): CheckEmailResponse => ({
    exists: response.exists ?? false,
    firstName: response.firstName,
    lastName: response.lastName,
    isActiveDemonstrator: response.isActiveDemonstrator ?? false,
    isWithinCooldown: response.isWithinCoolDown ?? false,
    cooldownEndDate: response.cooldownEndDate,
  })

  const checkEmail = async (email: string) => {
    loading.value = true
    error.value = null
    // If the email changed, reset session state for the new recruit attempt
    // but preserve the team leader selection (could be from a URL preset)
    if (session.value.email && session.value.email !== email) {
      const teamLeaderId = session.value.teamLeaderId
      const teamLeader = session.value.teamLeader
      session.value = createEmptySession(teamLeaderId)
      session.value.teamLeader = teamLeader
      session.value.email = email
      clearRecruitCookie()
      try {
        const { setUserRole } = useSAuth()
        setUserRole(UserRole.Anonymous)
      } catch { /* intentional no-op */ }
    }
    try {
      const response = await $api()<CheckEmailRaw>(`join/check-email/${encodeURIComponent(email)}`, {
        method: 'POST',
      })
      checkEmailResult.value = mapCheckEmailResponse(response)
      session.value.email = email
      if (checkEmailResult.value.firstName) {
        session.value.firstName = checkEmailResult.value.firstName
      }
      if (checkEmailResult.value.lastName) {
        session.value.lastName = checkEmailResult.value.lastName
      }
      return checkEmailResult.value
    } catch (e) {
      // 409 Conflict = existing customer found; the body is still a valid result.
      const data = getApiErrorData<CheckEmailRaw>(e)
      if (getErrorStatus(e) === 409 && data) {
        checkEmailResult.value = mapCheckEmailResponse(data)
        session.value.email = email
        if (checkEmailResult.value.firstName) {
          session.value.firstName = checkEmailResult.value.firstName
        }
        if (checkEmailResult.value.lastName) {
          session.value.lastName = checkEmailResult.value.lastName
        }

        // If backend returned errors (e.g. active demo, cooldown), display on the modal
        if (data.errors && data.errors.length > 0) {
          recordFailure(e)
          return undefined
        }

        return checkEmailResult.value
      }
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const fetchKitOptions = async () => {
    loading.value = true
    error.value = null
    try {
      // The agent's viewAsDate, so a future kit and its offer show as of that date (the
      // backend keeps it only for an agent or an agent's test recruit). Only that key:
      // addCommonQueryParam also carries cart keys join/kits does not take. Absent, not null,
      // when unset — ofetch sends null as an empty ?viewAsDate=. AB#64475
      const { viewAsDate } = addCommonQueryParam()
      const response = viewAsDate
        ? await $api()<JoinKitOptionsResponse>('join/kits', { query: { viewAsDate } })
        : await $api()<JoinKitOptionsResponse>('join/kits')
      kitOptions.value = response.options
      offerEndDate.value = response.offerEndDate
      session.value.hasMultipleKits = response.options.length > 1
      return response
    } catch (e) {
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const selectKit = (itemId: string) => {
    session.value.selectedKitItemId = itemId
  }

  const setStep = (step: JoinStep) => {
    session.value.currentStep = step
    trackJoinEvent('join-step', { step: JoinStep[step], stepIndex: String(step) })
  }

  const setTeamLeaderId = (id: string | null) => {
    session.value.teamLeaderId = id
  }

  const selectTeamLeader = (demonstrator: Demonstrator, isPreset = false) => {
    session.value.teamLeaderId = String(demonstrator.demoId)
    session.value.teamLeader = {
      demoId: demonstrator.demoId,
      firstName: demonstrator.firstName,
      lastName: demonstrator.lastName,
      city: demonstrator.city,
      suburb: demonstrator.suburb,
      county: demonstrator.county,
      region: demonstrator.region,
      phoneNumber: demonstrator.phoneNumber,
      email: demonstrator.email,
      socialMedia: demonstrator.socialMedia ? { ...demonstrator.socialMedia } : undefined,
      isPreset,
      confirmedAt: null,
    }
  }

  const confirmTeamLeader = () => {
    if (session.value.teamLeader) {
      session.value.teamLeader.confirmedAt = new Date().toISOString()
    }
  }

  const fetchTeamLeaderById = async (demoId: number | string) => {
    loading.value = true
    error.value = null
    try {
      const results = await $api()<Demonstrator[]>(`demonstrator/profiles?demoId=${demoId}`)
      if (Array.isArray(results) && results.length > 0 && results[0]) {
        selectTeamLeader(results[0], true)
        return session.value.teamLeader
      }
      // Demo not found (dropped or invalid ID) — clear the preset
      error.value = 'ida.error.teamLeaderNotFound'
      return undefined
    } catch (e) {
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const clearTeamLeader = () => {
    session.value.teamLeaderId = null
    session.value.teamLeader = null
  }

  // What both ways into a recruit session share -- join/verify-customer for a real applicant
  // and join/agent-recruit for an agent's test recruit: the isRecruit cookie recruit-guard
  // reads, the Recruit role auth gates on, and whatever names are known. AB#64477
  const beginRecruitSession = ({ firstName, lastName }: { firstName?: string | null, lastName?: string | null }) => {
    setRecruitCookie()
    useSAuth().setUserRole(UserRole.Recruit)
    if (firstName) {
      session.value.firstName = firstName
    }
    if (lastName) {
      session.value.lastName = lastName
    }
  }

  const verifyCustomer = async (request: VerifyCustomerRequest) => {
    loading.value = true
    error.value = null
    try {
      // AB#61004: use the join-process endpoint so the recruit cart is seeded from the new
      // ~/join-kit-options kit (171861 + welcome box) rather than the legacy ~/ida-kit-options
      // kit (131052). The legacy ida/verify-customer remains for the nuxt2 join flow.
      await $api()('join/verify-customer', {
        method: 'POST',
        body: request,
      })
      // Populate the recruit name from the authenticated customer so the join
      // header shows their full name (check-email only returns first name).
      const { user } = useSAuth()
      beginRecruitSession({ firstName: user.value?.firstName, lastName: user.value?.lastName })
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  // The cookie, impersonation-context, role and session writes that turn the current
  // session into the agent's test recruit. Split out of startAsAgent so the start reads
  // as generate-email, call, mark. AB#64477
  const markSessionAsAgentRecruit = (email: string) => {
    usePersistentStore().startTestRecruitImpersonation(email)
    session.value = createEmptySession(session.value.teamLeaderId)
    session.value.email = email
    beginRecruitSession(TEST_RECRUIT)
  }

  // An agent starts the join as a throwaway test recruit, with no pop-up: no check-email,
  // registration or welcome-back step and no customer account. Ports nuxt2's AgentHeader
  // newRecruit() (random 15-character @gmail.com email, name agent agent, phone 12345678)
  // onto join/agent-recruit, which signs in a recruit with the agent as Actor so viewAsDate
  // applies and the backend refuses payment and submit. The agent context and the session
  // isAgentImpersonated cookie mark the session as the agent's, so a reload keeps it (see
  // init-auth.server.ts) and the exit returns to the agent rather than signing out. AB#64477
  const startAsAgent = async () => {
    loading.value = true
    error.value = null
    try {
      const email = generateTestRecruitEmail()
      const viewAsDate = usePersistentStore().getPersistentValue('viewAsDate')
      await $api()('join/agent-recruit', {
        method: 'POST',
        body: { email, ...TEST_RECRUIT },
        query: viewAsDate ? { viewAsDate } : undefined,
      })
      markSessionAsAgentRecruit(email)
      return true
    } catch (e) {
      const { listedErrors } = recordFailure(e)
      captureUnexpected(e, listedErrors, 'startAsAgent')
      return false
    } finally {
      loading.value = false
    }
  }

  // The agent leaves the join back to their own session (agent/clear-impersonation signs the
  // Actor back in and clears viewAsDate) -- not clearSession(), whose logout() would sign the
  // agent out entirely. The clear runs BEFORE local recruit state is dropped, and the result
  // says whether the agent is back: clearImpersonation rethrows what failed, and on a
  // real failure the recruit session is left intact so Exit can be pressed again. AB#64477
  const leaveAsAgent = async () => {
    try {
      await useAgentStore().clearImpersonation()
    } catch (e) {
      // A refresh after the restore failed: the agent is back, so the exit still completes.
      if (!(e instanceof ImpersonationRefreshError)) {
        error.value = GENERIC_ERROR
        captureUnexpected(e, undefined, 'leaveAsAgent')
        return false
      }
    }
    // clearJoinData, not resetSession: clearImpersonation has just restored the agent's role
    // (setUser on the agent account), and resetSession would set it back to Anonymous -- the
    // agent toolbar and gates would vanish until a reload.
    clearJoinData()
    return true
  }

  const storeUpline = async (demoId: number) => {
    loading.value = true
    error.value = null
    try {
      await $api()(`join/upline/${demoId}`, { method: 'PATCH' })
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  const acceptAgreements = async () => {
    loading.value = true
    error.value = null
    try {
      // Clicking ACCEPT always means the agreements are accepted, so persist
      // SignedAgreements=true. The demo-directory opt-in is a separate field saved
      // independently via saveDirectoryAgreement — it must NOT be sent here (doing so
      // persisted SignedAgreements=false when the box was unchecked, so the acceptance
      // was lost on refresh).
      await $api()('join/accept-agreements/true', { method: 'PATCH' })
      session.value.agreementsAccepted = true
      setStep(JoinStep.Agreements)
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  const saveDirectoryAgreement = async (accepted: boolean) => {
    loading.value = true
    error.value = null
    try {
      await $api()(`join/demo-directory-agreement/${accepted}`, { method: 'PATCH' })
      session.value.directoryOptIn = accepted
      if (recruit.value) {
        recruit.value.demoDirectoryAgreement = accepted
      }
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  const fetchRecruit = async () => {
    loading.value = true
    error.value = null
    try {
      const response = await $api()<RecruitRaw>('join')
      // API returns { httpStatusCode, recruit: { firstName, lastName, ... } }
      // with flat fields — map into the nested RecruitRecord shape the UI expects
      const r = response.recruit ?? response
      const kitItem = r.cart?.items?.find(i => i.itemType?.toLowerCase() === 'kit')
      const mapped: RecruitRecord = {
        personalInfo: {
          firstName: r.firstName ?? '',
          lastName: r.lastName ?? '',
          email: r.email ?? '',
          homePhone: r.homePhone ?? '',
          gender: r.gender ?? '',
          birthday: r.birthday ? (r.birthday.split('T')[0] ?? '') : '',
          formattedBirthday: r.formattedBirthday ?? '',
          identification: r.identification ?? '',
          secondaryLanguage: r.secondaryLanguage ?? '',
          birthCity: r.birthCity ?? '',
          birthDepartment: r.birthDepartment ?? '',
          birthCountry: r.birthCountry ?? '',
          nationality: r.nationality ?? '',
          driversLicense: r.driversLicense ?? '',
          driversLicenseState: r.driversLicenseState ?? '',
          hasGstNumber: r.hasGstNumber ?? false,
          gstNumber: r.gstNumber ?? '',
        },
        mailingAddress: (r.mailingAddress ?? null) as RecruitAddress,
        shippingAddress: (r.shippingAddress ?? null) as RecruitAddress,
        signedAgreements: r.signedAgreements ?? false,
        demoDirectoryAgreement: r.demoDirectoryAgreement ?? false,
        uplineId: r.uplineId ?? null,
        selectedKitItemId: kitItem?.productID ?? kitItem?.productId ?? kitItem?.itemId ?? null,
      }
      recruit.value = mapped
      session.value.directoryOptIn = mapped.demoDirectoryAgreement
      return mapped
    } catch (e) {
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const hydrateSession = async (): Promise<RecruitRecord | undefined> => {
    // Only hit the backend when the in-memory store is empty (a hard refresh /
    // cold load). During the normal SPA flow the session is already populated,
    // so skip the /join round-trip — step pages call this on every mount and we
    // don't want to refetch each time.
    if (session.value.email) return recruit.value ?? undefined

    // Capture the Nuxt app while we're still in the synchronous (in-context) part.
    // Under SSR the Nuxt instance is lost after the first await, so the $api() calls
    // inside fetchTeamLeaderById/fetchKitOptions (which call useNuxtApp internally)
    // must be re-entered with runWithContext or they throw "composable called
    // outside setup". fetchRecruit runs before any await, so it's already in context.
    const nuxtApp = useNuxtApp()
    const { setUserRole } = useSAuth()
    const record = await fetchRecruit()
    if (!record) return undefined

    // Recruits have no session endpoint: on a hard load the server's GET account 401s for
    // the recruit token, so auth is left Anonymous and the checkout service (picked by role)
    // then calls the customer checkout/invoice, whose 401 ends the join. The record just
    // loaded with that token, i.e. the backend accepted it as a recruit, so restore the role
    // sign-in sets (verifyCustomer). Decided by the backend's answer, not by the userRole
    // cookie, which auth deliberately does not trust (AB#63056). AB#64313
    setUserRole(UserRole.Recruit)

    session.value.email = record.personalInfo.email
    session.value.firstName = record.personalInfo.firstName
    session.value.lastName = record.personalInfo.lastName
    session.value.agreementsAccepted = record.signedAgreements
    session.value.directoryOptIn = record.demoDirectoryAgreement
    session.value.myDetailsSaved = !!record.mailingAddress?.addressLine1
    session.value.vatInfoCompleted = record.personalInfo.hasGstNumber
      ? !!record.personalInfo.gstNumber
      : true
    session.value.selectedKitItemId = record.selectedKitItemId

    if (record.uplineId) {
      const uplineId = record.uplineId
      await nuxtApp.runWithContext(() => fetchTeamLeaderById(uplineId))
    }

    if (!kitOptions.value) {
      await nuxtApp.runWithContext(() => fetchKitOptions())
    }

    return record
  }

  const savePersonalInfo = async (info: RecruitPersonalInfo) => {
    loading.value = true
    error.value = null
    try {
      const payload = withDepartmentCode(info)
      await $api()('join/personal-info', { method: 'PATCH', body: payload })
      if (!recruit.value) recruit.value = {} as RecruitRecord
      recruit.value.personalInfo = payload
      identificationRejection.value = null
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  const saveMailingAddress = async (address: RecruitAddress) => {
    loading.value = true
    error.value = null
    try {
      await $api()('join/mailing-address', { method: 'PATCH', body: address })
      if (!recruit.value) recruit.value = {} as RecruitRecord
      recruit.value.mailingAddress = address
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  const saveShippingAddress = async (address: RecruitAddress) => {
    loading.value = true
    error.value = null
    try {
      await $api()('join/shipping-address', { method: 'PATCH', body: address })
      if (!recruit.value) recruit.value = {} as RecruitRecord
      recruit.value.shippingAddress = address
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  const saveVatInfo = async (info: VatInfo) => {
    loading.value = true
    error.value = null
    try {
      await $api()('join/personal-info', {
        method: 'PATCH',
        body: { hasGstNumber: info.hasGstNumber, gstNumber: info.gstNumber },
      })
      if (!recruit.value) recruit.value = {} as RecruitRecord
      if (!recruit.value.personalInfo) recruit.value.personalInfo = {} as RecruitPersonalInfo
      recruit.value.personalInfo.hasGstNumber = info.hasGstNumber
      recruit.value.personalInfo.gstNumber = info.gstNumber
      return true
    } catch (e) {
      recordFailure(e)
      return false
    } finally {
      loading.value = false
    }
  }

  // Reports the myDetailsCompleted computed (see personalInfoComplete) and records the step only
  // when it is true. AB#64917
  const completeMyDetails = () => {
    session.value.myDetailsSaved = true
    if (!myDetailsCompleted.value) return false
    setStep(JoinStep.MyDetails)
    return true
  }

  const completeVatInfo = () => {
    session.value.vatInfoCompleted = true
    setStep(JoinStep.Vat)
  }

  const fetchJoinInvoice = async () => {
    loading.value = true
    error.value = null
    try {
      const checkoutStore = useCheckoutStore()
      await checkoutStore.fetchInvoice()
      return checkoutStore.invoice
    } catch (e) {
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const submitJoinOrder = async (checkoutProperties?: CheckoutProperties) => {
    loading.value = true
    error.value = null
    try {
      const checkoutStore = useCheckoutStore()
      // checkoutProperties carries the recurlyToken for the post-3DS resubmit
      // (combined actionTokenId|scaToken). #61137
      const response = await checkoutStore.submitOrder(checkoutProperties)
      trackJoinEvent('join-order-submitted', { invoiceId: session.value.invoiceId ?? '' })
      return response
    } catch (e) {
      const { message, listedErrors } = recordFailure(e)
      trackJoinEvent('join-order-failed', { message })
      // See identificationRejection. AB#64917
      if (hasApiErrorCode(e, RECRUIT_IDENTIFICATION_REQUIRED)) {
        identificationRejection.value = message
        session.value.myDetailsSaved = false
      }
      captureUnexpected(e, listedErrors, 'submitJoinOrder')
      return undefined
    } finally {
      loading.value = false
    }
  }

  const fetchConfirmation = async (invoiceId?: string) => {
    const id = invoiceId ?? session.value.invoiceId
    if (!id) {
      error.value = 'ida.error.noInvoice'
      return undefined
    }
    loading.value = true
    error.value = null
    try {
      const response = await $api()<JoinOrderConfirmation>(`join/checkout/invoice/confirmation/${id}`)
      confirmation.value = response
      return response
    } catch (e) {
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const checkWelcomeBoxEligibility = async () => {
    loading.value = true
    error.value = null
    try {
      const response = await $api()<WelcomeBoxEligibility>('join/order-history', {
        query: { email: session.value.email },
      })
      welcomeBoxEligibility.value = response
      return response
    } catch (e) {
      recordFailure(e)
      return undefined
    } finally {
      loading.value = false
    }
  }

  const clearSession = async () => {
    clearJoinData()
    try {
      const { setUserRole, logout } = useSAuth()
      setUserRole(UserRole.Anonymous)
      // pushHome=false: every exit that calls this navigates next with
      // redirectWithReload('/'), so letting logout also location.replace(home)
      // fired two navigations. It also stops confirmation.vue's
      // onBeforeUnmount — which calls this unawaited while the user is already
      // leaving — from hijacking that navigation and forcing them home.
      await logout(false)
    } catch { /* intentional no-op */ }
  }

  // The join's own data and the recruit cookie, without touching the user role.
  const clearJoinData = (teamLeaderId: string | null = null) => {
    session.value = createEmptySession(teamLeaderId)
    checkEmailResult.value = null
    confirmation.value = null
    recruit.value = null
    kitOptions.value = null
    offerEndDate.value = null
    welcomeBoxEligibility.value = null
    error.value = null
    identificationRejection.value = null
    clearRecruitCookie()
  }

  const resetSession = (teamLeaderId: string | null = null) => {
    clearJoinData(teamLeaderId)
    try {
      const { setUserRole } = useSAuth()
      setUserRole(UserRole.Anonymous)
    } catch { /* intentional no-op */ }
  }

  return {
    session,
    checkEmailResult,
    confirmation,
    recruit,
    kitOptions,
    offerEndDate,
    welcomeBoxEligibility,
    loading,
    error,
    identificationRejection,
    isExistingCustomer,
    isActiveDemonstrator,
    isWithinCooldown,
    cooldownEndDate,
    firstName,
    setRecruitName,
    hasConfirmedTeamLeader,
    hasSelectedKit,
    selectedKit,
    agreementsAccepted,
    myDetailsCompleted,
    vatInfoCompleted,
    checkEmail,
    fetchKitOptions,
    selectKit,
    checkWelcomeBoxEligibility,
    setStep,
    setTeamLeaderId,
    selectTeamLeader,
    confirmTeamLeader,
    fetchTeamLeaderById,
    clearTeamLeader,
    verifyCustomer,
    startAsAgent,
    leaveAsAgent,
    storeUpline,
    acceptAgreements,
    saveDirectoryAgreement,
    fetchRecruit,
    hydrateSession,
    savePersonalInfo,
    saveMailingAddress,
    saveShippingAddress,
    saveVatInfo,
    completeMyDetails,
    completeVatInfo,
    fetchJoinInvoice,
    submitJoinOrder,
    fetchConfirmation,
    clearSession,
    resetSession,
  }
})
